import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Edit2, ListChecks, Plus, Trash2, X } from "lucide-react";
import { PageLoader } from "#/components/common/PageLoader";
import { ErrorState } from "#/components/common/ErrorState";
import { useConfirm } from "#/components/common/ConfirmProvider";
import {
  useAllTrainingCategories,
  useCreateCategory,
  useDeleteCategory,
  useToggleCategoryActive,
  useUpdateCategory,
} from "../hooks";
import type { TrainingCategory, TrainingCategoryCreateRequest } from "../types";

// ─── helpers ──────────────────────────────────────────────────────────────────

function extractMsg(err: unknown) {
  const e = err as any;
  return e?.response?.data?.message ?? e?.message ?? "Something went wrong.";
}

const EMPTY_CAT: TrainingCategoryCreateRequest = { code: "", name: "", scope: "", active: true };

// ─── Category row ─────────────────────────────────────────────────────────────

function CategoryRow({
  cat,
  onEdit,
}: {
  cat: TrainingCategory;
  onEdit: (cat: TrainingCategory) => void;
}) {
  const del    = useDeleteCategory();
  const toggle = useToggleCategoryActive();
  const confirm = useConfirm();
  const busy   = del.isPending || toggle.isPending;

  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-slate-50">
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-slate-800 text-sm">{cat.name}</p>
        <p className="text-xs text-slate-400">
          {cat.code}{cat.scope ? ` · ${cat.scope}` : ""}
        </p>
      </div>

      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
        cat.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
      }`}>
        {cat.active ? "Active" : "Inactive"}
      </span>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => toggle.mutate({ id: cat.id, name: cat.name, scope: cat.scope, active: cat.active })}
          disabled={busy}
          title={cat.active ? "Deactivate" : "Activate"}
          className={`rounded-lg px-2 py-1 text-xs font-semibold disabled:opacity-50 transition-colors ${
            cat.active
              ? "text-amber-600 hover:bg-amber-50"
              : "text-emerald-600 hover:bg-emerald-50"
          }`}
        >
          {cat.active ? "Deactivate" : "Activate"}
        </button>
        <button
          type="button"
          onClick={() => onEdit(cat)}
          disabled={busy}
          className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
        >
          <Edit2 className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={async () => {
            if (await confirm(`Delete category "${cat.name}"? This cannot be undone.`)) {
              del.mutate(cat.id);
            }
          }}
          disabled={busy}
          className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ─── Category form ────────────────────────────────────────────────────────────

function CategoryForm({
  initial,
  onSave,
  onCancel,
  busy,
  error,
}: {
  initial: TrainingCategoryCreateRequest;
  onSave: (v: TrainingCategoryCreateRequest) => void;
  onCancel: () => void;
  busy?: boolean;
  error?: string | null;
}) {
  const [form, setForm] = useState(initial);

  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4 space-y-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="block space-y-1">
          <span className="text-xs font-semibold text-slate-600">Code <span className="text-red-500">*</span></span>
          <input
            className="input"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s+/g, "_") })}
            placeholder="e.g. TECH"
            disabled={busy}
          />
        </label>
        <label className="block space-y-1 sm:col-span-1">
          <span className="text-xs font-semibold text-slate-600">Name <span className="text-red-500">*</span></span>
          <input
            className="input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Technical Skills"
            disabled={busy}
          />
        </label>
        <label className="block space-y-1">
          <span className="text-xs font-semibold text-slate-600">Scope</span>
          <input
            className="input"
            value={form.scope ?? ""}
            onChange={(e) => setForm({ ...form, scope: e.target.value || null })}
            placeholder="e.g. IT, Finance"
            disabled={busy}
          />
        </label>
      </div>
      {error && <p className="text-xs text-red-600 font-semibold">{error}</p>}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="rounded-xl border px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-white disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onSave(form)}
          disabled={busy || !form.code.trim() || !form.name.trim()}
          className="rounded-xl bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save Category"}
        </button>
      </div>
    </div>
  );
}

// ─── Mandatory training form ──────────────────────────────────────────────────

const EMPTY_MANDATORY: TrainingRequestCreateRequest = {
  trainingTitle: "",
  categoryId: null,
  trainingStartDate: null,
  trainingEndDate: null,
  providerName: null,
  venue: null,
  applicantEstimatedCost: null,
  expectedOutcome: null,
  mandatory: true,
};

function MandatoryTrainingForm({
  categories,
  onCreated,
}: {
  categories: TrainingCategory[];
  onCreated: () => void;
}) {
  const [form, setForm] = useState<TrainingRequestCreateRequest>(EMPTY_MANDATORY);
  const [error, setError] = useState<string | null>(null);
  const [submitNow, setSubmitNow] = useState(true);

  const create = useCreateTrainingRequest();
  const submit = useSubmitTrainingRequest();
  const busy   = create.isPending || submit.isPending;

  async function handleCreate() {
    setError(null);
    if (!form.trainingTitle.trim()) { setError("Title is required."); return; }
    if (!form.categoryId) { setError("Category is required."); return; }
    try {
      const req = await create.mutateAsync(form);
      if (submitNow) await submit.mutateAsync(req.id);
      setForm(EMPTY_MANDATORY);
      onCreated();
    } catch (err) { setError(extractMsg(err)); }
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block space-y-1.5 sm:col-span-2">
          <span className="text-sm font-semibold text-slate-700">Training Title <span className="text-red-500">*</span></span>
          <input
            className="input"
            value={form.trainingTitle}
            onChange={(e) => setForm({ ...form, trainingTitle: e.target.value })}
            placeholder="e.g. Annual Data Protection Awareness"
            disabled={busy}
          />
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-semibold text-slate-700">Category <span className="text-red-500">*</span></span>
          <select
            className="input"
            value={form.categoryId ?? ""}
            onChange={(e) => setForm({ ...form, categoryId: e.target.value ? Number(e.target.value) : null })}
            disabled={busy}
          >
            <option value="">— Select category —</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-semibold text-slate-700">Provider</span>
          <input
            className="input"
            value={form.providerName ?? ""}
            onChange={(e) => setForm({ ...form, providerName: e.target.value || null })}
            placeholder="e.g. HR Department"
            disabled={busy}
          />
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-semibold text-slate-700">Start Date</span>
          <input
            type="date"
            className="input"
            value={form.trainingStartDate ?? ""}
            onChange={(e) => setForm({ ...form, trainingStartDate: e.target.value || null })}
            disabled={busy}
          />
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-semibold text-slate-700">End Date</span>
          <input
            type="date"
            className="input"
            value={form.trainingEndDate ?? ""}
            min={form.trainingStartDate ?? undefined}
            onChange={(e) => setForm({ ...form, trainingEndDate: e.target.value || null })}
            disabled={busy}
          />
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-semibold text-slate-700">Venue / Location</span>
          <input
            className="input"
            value={form.venue ?? ""}
            onChange={(e) => setForm({ ...form, venue: e.target.value || null })}
            placeholder="e.g. Boardroom / Online"
            disabled={busy}
          />
        </label>
      </div>

      <label className="block space-y-1.5">
        <span className="text-sm font-semibold text-slate-700">Expected Outcome</span>
        <textarea
          className="input min-h-20 resize-y"
          value={form.expectedOutcome ?? ""}
          onChange={(e) => setForm({ ...form, expectedOutcome: e.target.value || null })}
          placeholder="What should all employees gain from this training?"
          disabled={busy}
        />
      </label>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={submitNow}
          onChange={(e) => setSubmitNow(e.target.checked)}
          disabled={busy}
          className="rounded"
        />
        <span className="text-sm text-slate-700">
          Submit for approval immediately (uncheck to save as draft first)
        </span>
      </label>

      {error && <p className="text-sm text-red-600 font-semibold">{error}</p>}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleCreate}
          disabled={busy}
          className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          {busy ? "Creating…" : "Create Mandatory Training"}
        </button>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function TrainingSetupPage() {
  const navigate = useNavigate();
  const catsQuery = useAllTrainingCategories();
  const createCat = useCreateCategory();
  const updateCat = useUpdateCategory();

  const [showNewCat, setShowNewCat] = useState(false);
  const [editCat, setEditCat]       = useState<TrainingCategory | null>(null);
  const [catError, setCatError]     = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const categories = catsQuery.data ?? [];

  if (catsQuery.isLoading) return <PageLoader label="Loading training setup…" />;
  if (catsQuery.isError)   return <ErrorState title="Failed to load" message={String(catsQuery.error)} />;

  async function handleCreateCategory(v: TrainingCategoryCreateRequest) {
    setCatError(null);
    try {
      await createCat.mutateAsync(v);
      setShowNewCat(false);
    } catch (err) { setCatError(extractMsg(err)); }
  }

  async function handleUpdateCategory(v: TrainingCategoryCreateRequest) {
    if (!editCat) return;
    setCatError(null);
    try {
      await updateCat.mutateAsync({ id: editCat.id, payload: v });
      setEditCat(null);
    } catch (err) { setCatError(extractMsg(err)); }
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Training Setup</h1>
        </div>
        <button
          onClick={() => navigate("/hr/training")}
          className="text-sm text-slate-500 hover:text-slate-800"
        >
          ← Back to Training
        </button>
      </div>

      {successMsg && (
        <div className="flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          {successMsg}
          <button onClick={() => setSuccessMsg(null)} className="ml-auto"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {/* ── Categories ── */}
      <div className="rounded-2xl border bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between border-b bg-slate-50 px-5 py-3">
          <div>
            <h2 className="font-bold text-slate-800">Training Categories</h2>
          </div>
          <button
            type="button"
            onClick={() => { setShowNewCat(true); setEditCat(null); setCatError(null); }}
            className="flex items-center gap-1.5 rounded-xl bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            <Plus className="h-4 w-4" /> Add Category
          </button>
        </div>

        {showNewCat && (
          <div className="p-4 border-b bg-slate-50/60">
            <CategoryForm
              initial={EMPTY_CAT}
              onSave={handleCreateCategory}
              onCancel={() => { setShowNewCat(false); setCatError(null); }}
              busy={createCat.isPending}
              error={catError}
            />
          </div>
        )}

        {editCat && (
          <div className="p-4 border-b bg-slate-50/60">
            <p className="text-xs font-semibold text-slate-500 mb-3">Editing: {editCat.name}</p>
            <CategoryForm
              initial={{ code: editCat.code, name: editCat.name, scope: editCat.scope, active: editCat.active }}
              onSave={handleUpdateCategory}
              onCancel={() => { setEditCat(null); setCatError(null); }}
              busy={updateCat.isPending}
              error={catError}
            />
          </div>
        )}

        {categories.length === 0 ? (
          <p className="px-5 py-6 text-sm text-slate-400">
            No categories yet. Add one above — the form dropdown won't work without at least one active category.
          </p>
        ) : (
          <div className="divide-y">
            {categories.map((c) => (
              <CategoryRow key={c.id} cat={c} onEdit={(cat) => { setEditCat(cat); setShowNewCat(false); setCatError(null); }} />
            ))}
          </div>
        )}
      </div>

      {/* ── Programs shortcut ── */}
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <ListChecks className="h-6 w-6 text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-slate-800">Training Programs</p>
            <p className="text-sm text-slate-600 mt-0.5">
              Create organisation-wide training programs and publish them to enrol all employees, specific departments, or individuals. Attendance is tracked per enrollment.
            </p>
          </div>
        </div>
        <Link
          to="/hr/training/programs"
          className="shrink-0 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700"
        >
          Manage Programs →
        </Link>
      </div>
    </div>
  );
}
